import { Box } from '@mui/material'
import { useContext, useEffect, useMemo } from 'react'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { resolveDefaultPhoneCountryIso } from 'src/shared/patient/phoneCountry'
// Hooks
import { usePatientForm } from './hooks/usePatientForm'
import { usePatientValidation } from './hooks/usePatientValidation'
import { usePatientSearch } from './hooks/usePatientSearch'
import { useFormFieldVisibility } from './hooks/useFormFieldVisibility'
// Components
import PatientConsentSection from './components/PatientConsentSection'
import PatientEssentialsSection from './components/PatientEssentialsSection'
import PatientMoreDetailsSection from './components/PatientMoreDetailsSection'
import PatientTagsField from './components/PatientTagsField'
import {
  PatientFormField,
  PatientFormGrid,
  PatientNativeSelect,
} from './components/PatientFormPrimitives'
import { CONSENT_MODE_NONE } from './utils/patientConsentUtils'
import { buildPatientFormI18n, PatientFormI18nContext } from './utils/patientFormI18n'

/**
 * Shared patient create / edit / view form used by every portal (practitioner chamber and
 * bookings, clinic assistant, support). Hosts own the patient state and the submit button;
 * this component only renders fields and reports validity through the state it is given.
 */
export default function PatientFormComponentV2({
  editMode = false,
  patientDetails,
  setPatientDetails,
  hidePatientNumber = false,
  hideInputValidations = false,
  makeAnonymousPatient,
  onFindingExistingPatient,
  addRelatedPatient,
  hideRelationShipDetails = false,
  clinicId,
  clinicBrandId,
  setMakeAnonymousPatient,
  importIdFieldLabel = 'Import Id',
  isAllowedToAddImportId = false,
  isAllowedToAddUHID = false,
  isForcingAnonymousPatient = false,
  moveEmailToMoreSettings = false,
  disablePatientSearch = false,
  showAdminFields = false,
  dontUseMoreDetailsAccordian,
  autoFocusMobileField = false,
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
  formUiLanguageCode = 'en',
  onChangeFormUiLanguage,
  showFormUiLanguageSelector = false,
  // Hosts can flip this (e.g. after a failed submit attempt) to surface every error at once.
  revealAllValidationErrors = false,
  // Clinic-assistant flavour: only mobile / name / gender / age up front, email in More details.
  compactEssentials = false,
  // Clinic record (any shape carrying a country) — decides the assumed phone country code.
  clinicData,
  // Brand's import sources ({id,title}) for the Identifiers group.
  importedFromItems = [],
  // Base path of the patient-tags API for this portal (enables brand tag suggestions).
  patientTagsApiSignature,
  showTags = true,
  // 'clinical' (checkbox required), 'support' (pending banner) or 'none' (edit/view forms).
  consentMode = CONSENT_MODE_NONE,
  // Bump to scroll the consent box into view and highlight it (user tried to submit without it).
  consentNudgeToken = 0,
  // 'inline' renders the consent block at the end of the form; 'footer' means the host renders
  // PatientConsentStrip inside its pinned footer instead.
  consentPlacement = 'inline',
  showDocuments = true,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const patientFormI18n = useMemo(
    () => buildPatientFormI18n(formUiLanguageCode, unSecureSetupData?.appLanguages),
    [formUiLanguageCode, unSecureSetupData?.appLanguages],
  )

  const formHook = usePatientForm(
    patientDetails,
    setPatientDetails,
    makeAnonymousPatient,
    setMakeAnonymousPatient,
  )

  const validationHook = usePatientValidation(
    patientDetails,
    makeAnonymousPatient,
    hideInputValidations,
    patientFormI18n.translateValidationMessage,
    { revealAllErrors: revealAllValidationErrors },
  )

  const searchHook = usePatientSearch(
    patientDetails,
    clinicId,
    clinicBrandId,
    addRelatedPatient,
    onFindingExistingPatient,
    setMakeAnonymousPatient,
    patientSearchApiSignature,
  )

  const countryIso = useMemo(
    () => resolveDefaultPhoneCountryIso({ clinicData, fallbackIso: patientDetails?.countryCode }),
    [clinicData, patientDetails?.countryCode],
  )
  // Remember the assumed country on the form state so the payload builder can complete a
  // number typed without its country code.
  useEffect(() => {
    if (countryIso && patientDetails && patientDetails.defaultCountryIso !== countryIso) {
      setPatientDetails?.((prev) => (prev ? { ...prev, defaultCountryIso: countryIso } : prev))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIso])

  const visibilityHook = useFormFieldVisibility({
    patientDetails,
    makeAnonymousPatient,
    isForcingAnonymousPatient,
    moveEmailToMoreSettings,
    hidePatientNumber,
    isAllowedToAddUHID,
    isAllowedToAddImportId,
    hideRelationShipDetails,
    showAdminFields,
    compactEssentials,
  })

  return (
    <PatientFormI18nContext.Provider value={patientFormI18n}>
      <Box
        className="patient-form-v2"
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 }}
      >
        {showFormUiLanguageSelector && (
          <PatientFormGrid>
            <PatientFormField
              label={patientFormI18n.copy.formLanguageLabel}
              span={{ xs: 12, md: 5 }}
              htmlFor="patient-form-ui-language"
            >
              <PatientNativeSelect
                id="patient-form-ui-language"
                disabled={!editMode || !onChangeFormUiLanguage}
                value={patientFormI18n.uiLanguageCode}
                options={patientFormI18n.uiLanguageOptions}
                getOptionValue={(option) => option?.id}
                getOptionLabel={(option) =>
                  option?.id === 'en'
                    ? option?.language
                    : `${option?.language} (${option?.localLanguageName})`
                }
                onChange={(event) => onChangeFormUiLanguage?.(event.target.value)}
              />
            </PatientFormField>
          </PatientFormGrid>
        )}

        <PatientEssentialsSection
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          validationHook={validationHook}
          searchHook={searchHook}
          visibilityHook={visibilityHook}
          makeAnonymousPatient={makeAnonymousPatient}
          isForcingAnonymousPatient={isForcingAnonymousPatient}
          importIdFieldLabel={importIdFieldLabel}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          patientSearchApiSignature={patientSearchApiSignature}
          disablePatientSearch={disablePatientSearch}
          autoFocusMobileField={autoFocusMobileField}
          canToggleNoContact={typeof setMakeAnonymousPatient === 'function'}
          countryIso={countryIso}
        />

        {showTags && (
          <PatientFormGrid>
            <PatientTagsField
              tags={patientDetails?.tags}
              editMode={editMode}
              onAdd={formHook.addTag}
              onRemove={formHook.removeTag}
              patientTagsApiSignature={patientTagsApiSignature}
              clinicBrandId={clinicBrandId}
              trailingLabel={patientFormI18n.copy.preferredLanguageShortLabel}
              trailing={
                visibilityHook.isFieldVisible('language') ? (
                  <PatientNativeSelect
                    id="patient-language"
                    aria-label={patientFormI18n.copy.patientPreferredLanguageLabel}
                    title={patientFormI18n.copy.patientPreferredLanguageLabel}
                    value={patientDetails?.defaultCommunicationLanguage || ''}
                    onChange={formHook.handleLanguageChange}
                    disabled={!editMode}
                    options={unSecureSetupData?.appLanguages || []}
                    getOptionValue={(option) => option?.id}
                    getOptionLabel={(option) =>
                      option?.id !== 'en' && option?.localLanguageName
                        ? `${option?.language} (${option?.localLanguageName})`
                        : option?.language
                    }
                    sx={{ fontSize: { xs: '14px', sm: '13px' }, color: '#475569', paddingLeft: '10px' }}
                  />
                ) : null
              }
            />
          </PatientFormGrid>
        )}

        {visibilityHook.isFieldVisible('moreDetailsSection') && (
          <PatientMoreDetailsSection
            dontUseMoreDetailsAccordian={dontUseMoreDetailsAccordian}
            patientDetails={patientDetails}
            editMode={editMode}
            formHook={formHook}
            validationHook={validationHook}
            visibilityHook={visibilityHook}
            makeAnonymousPatient={makeAnonymousPatient}
            importIdFieldLabel={importIdFieldLabel}
            importedFromItems={importedFromItems}
            identifiersInEssentials={Boolean(isAllowedToAddUHID || isAllowedToAddImportId)}
            showDocuments={showDocuments}
          />
        )}

        {consentPlacement !== 'footer' && (
          <PatientConsentSection
            mode={consentMode}
            patientDetails={patientDetails}
            editMode={editMode}
            onChange={formHook.handleConsentChange}
            nudgeToken={consentNudgeToken}
          />
        )}
      </Box>
    </PatientFormI18nContext.Provider>
  )
}
